import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Profile } from '@/components/icons';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatSupportDate, SUPPORT_CATEGORY_LABELS } from '../constants';
import { SupportStatusBadge } from './SupportStatusBadge';
import type { SupportTicketSummary } from '@/api/support';

type SupportTicketCardProps = {
    ticket: SupportTicketSummary;
    to?: string;
    selected?: boolean;
    perspective?: 'user' | 'agent';
    onClick?: () => void;
};

export function SupportTicketCard({
    ticket,
    to,
    selected = false,
    perspective = 'user',
    onClick,
}: SupportTicketCardProps) {
    const content = (
        <Card
            className={cn(
                'group border-border/80 bg-card/95 p-5 shadow-elevation-1 transition hover:-translate-y-0.5 hover:border-brand/25 hover:shadow-elevation-2',
                selected && 'border-brand/40 bg-brand-subtle/40',
            )}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <SupportStatusBadge status={ticket.status} perspective={perspective} />
                        <span className="rounded-md bg-surface-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                            {SUPPORT_CATEGORY_LABELS[ticket.category]}
                        </span>
                    </div>
                    <div className="space-y-1">
                        <h3 className="line-clamp-2 break-words text-base font-semibold leading-snug text-foreground">
                            {ticket.subject}
                        </h3>
                        {/* A bare text node cannot truncate: an unbroken e-mail would
                            set the card's min-content width and blow out the column
                            it sits in. The span is what gives `truncate` something
                            to clamp. */}
                        {ticket.requester ? (
                            <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                                <Profile className="size-3.5 shrink-0" />
                                <span className="min-w-0 truncate">
                                    {ticket.requester.full_name} · {ticket.requester.email}
                                </span>
                            </p>
                        ) : null}
                    </div>
                    <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="size-3.5 shrink-0" />
                        <span className="min-w-0 truncate">
                            Atualizado em {formatSupportDate(ticket.updated_at)}
                        </span>
                    </p>
                </div>
                <ArrowRight className="mt-1 size-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-brand" />
            </div>
        </Card>
    );

    if (to) {
        return (
            <Link to={to} className="block">
                {content}
            </Link>
        );
    }

    return (
        <button type="button" className="block w-full text-left" onClick={onClick}>
            {content}
        </button>
    );
}
